import {
  FETCH_PLAN_ENDPOINTS_SUCCESS,
  ENDPOINT_CREATE,
  ENDPOINTS_MODAL_SHOW,
  ENDPOINTS_MODAL_HIDE,
  ENDPOINT_DELETE_SUCCESS,
  ENDPOINT_SAVE_SUCCESS,
  SINGLE_ENDPOINT_LOAD_SUCCESS,
  LOAD_USAGE_IN_BACKGROUND_SUCCESS,
  LOAD_USAGE_IN_BACKGROUND_START,
  VERIFY_CREDENTIALS,
  ENDPOINT_OAUTH_CONNECT,
  GWT_ENDPOINT_GET_XMATTERS_ASSIGNEE_START,
  GWT_ENDPOINT_GET_XMATTERS_ASSIGNEE_SUCCESS,
  GWT_ENDPOINT_GET_XMATTERS_ASSIGNEE_FAIL,
  credsVerifiedEnum,
} from 'actions/endpointsModal';
import { sortEndpoints } from 'selectors/endpointsModal';
import authTypes from 'xm-constants/authenticationTypes';
import { upsertById } from 'reducers/flowDesigner/utils';
import { OAuthGrantTypes } from '@xmatters/common/constants/endpoints';

export const initialState = {
  endpoints: [],
  authTypeFilter: null,
  isNew: false,
  // This contains the pristine content of the endpoint. Redux form uses this to reset content if needed.
  selectedEndpointOriginalValue: null,
  show: false,
  credsVerified: credsVerifiedEnum.NOT_VERIFIED,
  loading: false,
};

// We don't actually save 'BASIC' grantType to the DB so oauthGrantType is null coming from xmapi
// initialize it to 'BASIC' in this case
const initializeGrantType = grantType => grantType || OAuthGrantTypes.BASIC;

// Need to give them empty initial values instead of undefined so map's key and Array find won't break.
export const emptyEndpoint = {
  id: '',
  name: '', // Default set by create action
  authenticationType: authTypes.BASIC,
  authentication: {
    oauthTokenPrefix: 'Bearer',
    oauthGrantType: OAuthGrantTypes.BASIC,
  },
  data: {
    // Initial value for TOKEN endpoints
    headerType: 'authorization',
  },
  preemptive: true,
};

// A single endpoint fetch can use ?embed=usage to retrieve its usage. Put that data in Redux store for later use.
function selectEndpoint(state, { payload }, loadUsageInBackground = false) {
  const newState = {
    ...state,
    endpoints: state.endpoints.map(endpoint =>
      endpoint.id === payload.id ? payload : endpoint,
    ),
    isNew: false,
    credsVerified: credsVerifiedEnum.NOT_VERIFIED,
    loading: false,
  };

  if (!loadUsageInBackground) {
    newState.selectedEndpointOriginalValue = payload;
    if (payload.authentication && payload.authentication.authScope) {
      // fixing the mismatch between "oauthScopes" that frontend legitimately expects
      // and "authScope" that backend shamefully delivers
      // (we're not fixing backend because backward compatibility
      // and because we don't want to send both oauthScopes and authScope apparently)
      newState.selectedEndpointOriginalValue.authentication.oauthScopes =
        payload.authentication.authScope;
    }
    if (!newState.selectedEndpointOriginalValue.authentication) {
      newState.selectedEndpointOriginalValue.authentication = {};
    }
    newState.selectedEndpointOriginalValue.authentication.oauthGrantType =
      initializeGrantType(payload?.authentication?.oauthGrantType);
  }
  return newState;
}

// When we save something, the return value doesn't contain the "usages" section. Add it back.
function saveRetrievedEndpoint(state, { payload }) {
  const payloadWithMisnomerFixes = {
    ...payload,
    authentication: {
      ...payload.authentication,
      oauthScopes: payload.authentication?.authScope, // see comment in "selectEndpoint" function
      oauthGrantType: initializeGrantType(
        payload.authentication?.oauthGrantType,
      ),
      // Blank out the client secret so the user cannot resubmit the encrypted value
      oauthClientSecret: '',
    },
  };

  // When we create a new endpoint, the client doesn't create the ID; the server gives it one.
  // Therefore, filter the client one out and put in the server augmented one.
  const correctedEndpoints = upsertById(
    state.endpoints,
    payloadWithMisnomerFixes.id,
    prevItem => ({
      ...payloadWithMisnomerFixes,
      usage: prevItem ? prevItem.usage : null,
    }),
  );

  const sortedEndpoints = sortEndpoints(correctedEndpoints);
  return {
    ...state,
    endpoints: sortedEndpoints,
    isNew: false,
    selectedEndpointOriginalValue: state.isNew
      ? payloadWithMisnomerFixes
      : correctedEndpoints.find(
          endpoint => endpoint.id === state.selectedEndpointOriginalValue.id,
        ),
  };
}

/**
 * If we open the EndpointsModal from a step configuration screen (for example, Slack - Create Channel)
 * we only show the relevant endpoint types (in this case, endpoints with OAUTH_SLACK_V2 authentication type).
 *
 * The filtering is mainly done in the getEndpoints selector; this reducer is only needed for the selected endpoint form content,
 * namely selectedEndpointOriginalValue.
 */
function selectFirst(state, action) {
  const { authTypeFilter } = action.payload;
  const { endpoints = [] } = state;

  const filteredEndpoints = authTypeFilter
    ? endpoints.filter(
        endpoint => endpoint.authenticationType === authTypeFilter,
      )
    : endpoints;

  const initGrantTypeOnEndpoint = endpoint => ({
    ...endpoint,
    authentication: {
      ...endpoint?.authentication,
      oauthGrantType: initializeGrantType(
        endpoint?.authentication?.oauthGrantType,
      ),
    },
  });

  return {
    ...state,
    authTypeFilter,

    // If there is at least 1 endpoint, select the 1st one by default.
    selectedEndpointOriginalValue: filteredEndpoints.length
      ? initGrantTypeOnEndpoint(filteredEndpoints[0])
      : null,
    show: true,
  };
}

export default function endpointsModalReducer(state = initialState, action) {
  switch (action.type) {
    case FETCH_PLAN_ENDPOINTS_SUCCESS:
      return {
        ...state,
        endpoints: sortEndpoints(action.payload.data),
      };
    case ENDPOINT_CREATE: {
      const authenticationType = action.payload.authType || authTypes.BASIC;
      return {
        ...state,
        selectedEndpointOriginalValue: {
          ...emptyEndpoint,
          name: action.payload.defaultName,
          authenticationType,
        },
        credsVerified: credsVerifiedEnum.NOT_VERIFIED,
        isNew: true,
      };
    }
    case ENDPOINTS_MODAL_SHOW: {
      return selectFirst(state, action);
    }
    case ENDPOINTS_MODAL_HIDE:
      return {
        ...state,
        authTypeFilter: null,
        isNew: false,
        show: false,
      };
    case ENDPOINT_DELETE_SUCCESS:
      return {
        ...state,
        endpoints: state.endpoints.filter(
          endpoint => endpoint.id !== action.payload.id,
        ),
      };
    case ENDPOINT_SAVE_SUCCESS:
      return saveRetrievedEndpoint(state, action);
    case SINGLE_ENDPOINT_LOAD_SUCCESS:
      return selectEndpoint(state, action);
    case LOAD_USAGE_IN_BACKGROUND_SUCCESS:
      return selectEndpoint(state, action, true);
    case LOAD_USAGE_IN_BACKGROUND_START:
      return {
        ...state,
        loading: true,
      };
    case VERIFY_CREDENTIALS: {
      return {
        ...state,
        credsVerified: action.verified,
        verificationMessage: action.message,
      };
    }
    case ENDPOINT_OAUTH_CONNECT: {
      // Conditionally update the selected cached endpoint value
      const selectedEndpointOriginalValue =
        action.id !== state.selectedEndpointOriginalValue.id
          ? state.selectedEndpointOriginalValue
          : {
              ...state.selectedEndpointOriginalValue,
              authentication: {
                ...state.selectedEndpointOriginalValue.authentication,
                connectionStatus: action.status,
              },
            };

      return {
        ...state,
        endpoints: upsertById(state.endpoints, action.id, prevItem => ({
          ...prevItem,
          authentication: {
            ...prevItem.authentication,
            connectionStatus: action.status,
          },
        })),
        selectedEndpointOriginalValue,
      };
    }

    case GWT_ENDPOINT_GET_XMATTERS_ASSIGNEE_START: {
      return {
        ...state,
        xmattersAssignee: null,
        xmattersAssigneeError: null,
        xmattersAssigneeIsLoading: true,
      };
    }
    case GWT_ENDPOINT_GET_XMATTERS_ASSIGNEE_SUCCESS: {
      return {
        ...state,
        xmattersAssigneeIsLoading: false,
        xmattersAssignee: action.payload,
      };
    }
    case GWT_ENDPOINT_GET_XMATTERS_ASSIGNEE_FAIL: {
      return {
        ...state,
        xmattersAssigneeIsLoading: false,
        xmattersAssigneeError: action.payload || true,
      };
    }

    default:
      return state;
  }
}
